import {
	closestCenter,
	DndContext,
	DragEndEvent,
	DragOverEvent,
	DragOverlay,
	DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	UniqueIdentifier,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, DotsVerticalOutline, HStack, InformationCircleOutline, rem, Text, Tooltip, VStack } from '@bonusly/design-system';

import { NavbarItemId } from '../../schemas/navigation';
import { normalizeTwoColumnLayout } from '../../utils/normalize-two-column-layout';

// We use dnd-kit instead of single-list reorder utilities because this editor
// must support two behaviors together: reordering within a section and dragging
// items across sections (Main navigation <-> More menu).

type NavbarSettingsEditorItem = {
	id: NavbarItemId;
	label: string;
	icon?: ReactNode;
	infoText?: string;
};

type FixedNavbarSettingsEditorItem = {
	id: string;
	label: string;
	icon?: ReactNode;
	infoText?: string;
};

type NavbarSettingsEditorProps = {
	initialMainNavItems: NavbarItemId[];
	initialMoreMenuItems: NavbarItemId[];
	items: NavbarSettingsEditorItem[];
	mainNavTitle: string;
	mainNavDescription: string;
	moreMenuTitle: string;
	moreMenuDescription: string;
	onSave: (mainNavItems: NavbarItemId[], moreMenuItems: NavbarItemId[]) => Promise<void>;
	onReset: () => Promise<void>;
	isSaving?: boolean;
	resetButtonLabel: string;
	saveButtonLabel?: string;
	note?: string;
	isResetDisabled?: boolean;
	onChange?: (mainNavItems: NavbarItemId[], moreMenuItems: NavbarItemId[]) => void;
	showSaveButton?: boolean;
	isStackedLayout?: boolean;
	dragHandlePosition?: 'left' | 'right';
	dragWholeItem?: boolean;
	showItemBorder?: boolean;
	showColumnBorder?: boolean;
	columnPadding?: number;
	fixedMainNavItems?: FixedNavbarSettingsEditorItem[];
	dragHandleIcon?: ReactNode;
	useCompanyValuesHandleStyle?: boolean;
	autoSaveOnDrop?: boolean;
};

type ContainerId = 'main' | 'more';
type ContainerState = Record<ContainerId, NavbarItemId[]>;

const MAIN_DROPPABLE_ID = 'navbar-settings-main-nav';
const MORE_DROPPABLE_ID = 'navbar-settings-more-menu';

const isContainerId = (id: string): id is typeof MAIN_DROPPABLE_ID | typeof MORE_DROPPABLE_ID => {
	return id === MAIN_DROPPABLE_ID || id === MORE_DROPPABLE_ID;
};

const arraysEqual = (left: NavbarItemId[], right: NavbarItemId[]) => {
	if (left.length !== right.length) {
		return false;
	}

	return left.every((value, index) => value === right[index]);
};

const normalizeItems = (mainNavItems: NavbarItemId[], moreMenuItems: NavbarItemId[], availableItemIds: NavbarItemId[]): ContainerState => {
	const normalizedLayout = normalizeTwoColumnLayout(mainNavItems, moreMenuItems, availableItemIds);
	return {
		main: normalizedLayout.mainItems,
		more: normalizedLayout.moreItems,
	};
};

const cloneContainerState = (state: ContainerState): ContainerState => ({
	main: [...state.main],
	more: [...state.more],
});

const getContainerForId = (id: UniqueIdentifier, state: ContainerState): ContainerId | null => {
	const parsedId = String(id);
	if (parsedId === MAIN_DROPPABLE_ID) {
		return 'main';
	}

	if (parsedId === MORE_DROPPABLE_ID) {
		return 'more';
	}

	if (state.main.includes(parsedId as NavbarItemId)) {
		return 'main';
	}

	if (state.more.includes(parsedId as NavbarItemId)) {
		return 'more';
	}

	return null;
};

const applyDragOperation = (currentState: ContainerState, activeId: UniqueIdentifier, overId: UniqueIdentifier): ContainerState => {
	const activeContainer = getContainerForId(activeId, currentState);
	const overContainer = getContainerForId(overId, currentState);

	if (!activeContainer || !overContainer) {
		return currentState;
	}

	const parsedActiveId = String(activeId) as NavbarItemId;
	const parsedOverId = String(overId);

	if (activeContainer === overContainer) {
		const containerItems = currentState[activeContainer];
		const oldIndex = containerItems.indexOf(parsedActiveId);
		const newIndex = isContainerId(parsedOverId) ? containerItems.length - 1 : containerItems.indexOf(parsedOverId as NavbarItemId);

		if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
			return currentState;
		}

		return {
			...currentState,
			[activeContainer]: arrayMove(containerItems, oldIndex, newIndex),
		};
	}

	const sourceItems = currentState[activeContainer];
	const destinationItems = currentState[overContainer];
	const sourceIndex = sourceItems.indexOf(parsedActiveId);

	if (sourceIndex < 0) {
		return currentState;
	}

	const nextSourceItems = sourceItems.filter((currentItemId) => currentItemId !== parsedActiveId);
	const overIndex = destinationItems.indexOf(parsedOverId as NavbarItemId);
	const destinationIndex = overIndex >= 0 ? overIndex : destinationItems.length;
	const nextDestinationItems = [...destinationItems];
	nextDestinationItems.splice(destinationIndex, 0, parsedActiveId);

	if (activeContainer === 'main') {
		return {
			main: nextSourceItems,
			more: nextDestinationItems,
		};
	}

	return {
		main: nextDestinationItems,
		more: nextSourceItems,
	};
};

export const shouldApplyDragOverOperation = (currentState: ContainerState, activeId: UniqueIdentifier, overId: UniqueIdentifier): boolean => {
	const activeContainer = getContainerForId(activeId, currentState);
	const overContainer = getContainerForId(overId, currentState);

	if (!activeContainer || !overContainer) {
		return false;
	}

	// Only apply cross-container updates while hovering to avoid reorder loops
	// caused by rapidly switching over item/container targets in the same list.
	return activeContainer !== overContainer;
};

type SortableItemProps = {
	id: NavbarItemId;
	label: string;
	icon?: ReactNode;
	infoText?: string;
	dragHandlePosition: 'left' | 'right';
	dragWholeItem: boolean;
	showItemBorder: boolean;
	dragHandleIcon?: ReactNode;
	useCompanyValuesHandleStyle: boolean;
};

const SortableItem: FC<SortableItemProps> = ({
	id,
	label,
	icon,
	infoText,
	dragHandlePosition,
	dragWholeItem,
	showItemBorder,
	dragHandleIcon,
	useCompanyValuesHandleStyle,
}) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<Box
			ref={setNodeRef}
			style={style}
			bg="bg.surface"
			borderWidth={showItemBorder ? '1px' : '0'}
			borderColor={showItemBorder ? (isDragging ? 'primary.fgStrong' : 'border.default') : 'transparent'}
			borderRadius="md"
			px={3}
			py={2}
			data-testid={`navbar-settings-item-${id}`}
			shadow={isDragging ? 'md' : 'none'}
			{...(dragWholeItem ? { ...attributes, ...listeners } : {})}
			{...(dragWholeItem ? { cursor: 'grab' } : {})}
		>
			<HStack spacing={3}>
				{dragHandlePosition === 'left' && (
					<Box
						{...(!dragWholeItem ? { ...attributes, ...listeners } : {})}
						cursor="grab"
						color="info.icon"
						display="inline-flex"
						alignItems="center"
						aria-label={`Reorder ${label}`}
						{...(useCompanyValuesHandleStyle
							? {
									_active: { cursor: 'grabbing' },
								}
							: {})}
					>
						{dragHandleIcon || <DotsVerticalOutline size="sm" />}
					</Box>
				)}
				{icon && (
					<Box color="text.default" display="inline-flex">
						{icon}
					</Box>
				)}
				<Text fontSize={100} color="text.default" fontWeight="medium">
					{label}
				</Text>
				{infoText && (
					<Tooltip label={infoText} placement="top">
						<Box color="text.softest" display="inline-flex" aria-label={`Info for ${label}`}>
							<InformationCircleOutline size="sm" />
						</Box>
					</Tooltip>
				)}
				{dragHandlePosition === 'right' && (
					<Box
						ml="auto"
						{...(!dragWholeItem ? { ...attributes, ...listeners } : {})}
						cursor="grab"
						color="info.icon"
						display="inline-flex"
						alignItems="center"
						aria-label={`Reorder ${label}`}
						{...(useCompanyValuesHandleStyle
							? {
									_active: { cursor: 'grabbing' },
								}
							: {})}
					>
						{dragHandleIcon || <DotsVerticalOutline size="sm" />}
					</Box>
				)}
			</HStack>
		</Box>
	);
};

type SortableColumnProps = {
	droppableId: string;
	title: string;
	description: string;
	items: NavbarSettingsEditorItem[];
	fixedItems?: FixedNavbarSettingsEditorItem[];
	dragHandlePosition: 'left' | 'right';
	dragWholeItem: boolean;
	showItemBorder: boolean;
	showColumnBorder: boolean;
	columnPadding: number;
	dragHandleIcon?: ReactNode;
	useCompanyValuesHandleStyle: boolean;
};

const SortableColumn: FC<SortableColumnProps> = ({
	droppableId,
	title,
	description,
	items,
	fixedItems = [],
	dragHandlePosition,
	dragWholeItem,
	showItemBorder,
	showColumnBorder,
	columnPadding,
	dragHandleIcon,
	useCompanyValuesHandleStyle,
}) => {
	const { setNodeRef, isOver } = useDroppable({
		id: droppableId,
	});

	return (
		<VStack align="stretch" spacing={3}>
			<Box>
				<Text fontSize={200} color="text.default" fontWeight="semibold" mb={1}>
					{title}
				</Text>
				{description ? (
					<Text fontSize={100} color="text.soft">
						{description}
					</Text>
				) : null}
			</Box>
			<Box
				ref={setNodeRef}
				bg={isOver ? 'secondary.bgAlpha' : 'bg.surface'}
				borderWidth={showColumnBorder ? '1px' : '0'}
				borderStyle={showColumnBorder ? 'dashed' : 'solid'}
				borderColor={showColumnBorder ? (isOver ? 'primary.fgStrong' : 'border.default') : 'transparent'}
				borderRadius="lg"
				p={columnPadding}
				minH={items.length === 0 ? rem(56) : rem(220)}
				transition="background-color 0.15s ease, border-color 0.15s ease"
				data-testid={droppableId}
			>
				<SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
					<VStack align="stretch" spacing={2}>
						{fixedItems.map((item) => (
							<Box key={`fixed-${item.id}`} bg="bg.surface" borderRadius="md" px={3} py={2}>
								<HStack spacing={3}>
									{item.icon && (
										<Box color="text.default" display="inline-flex">
											{item.icon}
										</Box>
									)}
									<Text fontSize={100} color="text.default" fontWeight="medium">
										{item.label}
									</Text>
									{item.infoText && (
										<Tooltip label={item.infoText} placement="top">
											<Box color="text.softest" display="inline-flex" aria-label={`Info for ${item.label}`}>
												<InformationCircleOutline size="sm" />
											</Box>
										</Tooltip>
									)}
								</HStack>
							</Box>
						))}
						{items.map((item) => (
							<SortableItem
								key={item.id}
								id={item.id}
								label={item.label}
								icon={item.icon}
								infoText={item.infoText}
								dragHandlePosition={dragHandlePosition}
								dragWholeItem={dragWholeItem}
								showItemBorder={showItemBorder}
								dragHandleIcon={dragHandleIcon}
								useCompanyValuesHandleStyle={useCompanyValuesHandleStyle}
							/>
						))}
					</VStack>
				</SortableContext>
			</Box>
		</VStack>
	);
};

export const NavbarSettingsEditor: FC<NavbarSettingsEditorProps> = ({
	initialMainNavItems,
	initialMoreMenuItems,
	items,
	mainNavTitle,
	mainNavDescription,
	moreMenuTitle,
	moreMenuDescription,
	onSave,
	onReset,
	isSaving = false,
	resetButtonLabel,
	saveButtonLabel = 'Save changes',
	note,
	isResetDisabled = false,
	onChange,
	showSaveButton = true,
	isStackedLayout = false,
	dragHandlePosition = 'left',
	dragWholeItem = false,
	showItemBorder = true,
	showColumnBorder = true,
	columnPadding = 3,
	fixedMainNavItems = [],
	dragHandleIcon,
	useCompanyValuesHandleStyle = false,
	autoSaveOnDrop = false,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const availableItemIds = useMemo(() => items.map((item) => item.id), [items]);
	const itemById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

	const initialState = useMemo(
		() => normalizeItems(initialMainNavItems, initialMoreMenuItems, availableItemIds),
		[initialMainNavItems, initialMoreMenuItems, availableItemIds]
	);

	const [state, setState] = useState<ContainerState>(initialState);
	const [activeItemId, setActiveItemId] = useState<NavbarItemId | null>(null);
	const [appliedInitialState, setAppliedInitialState] = useState(initialState);
	const stateRef = useRef(state);
	const dragStartStateRef = useRef<ContainerState | null>(null);

	useEffect(() => {
		stateRef.current = state;
	}, [state]);

	useEffect(() => {
		const initialChanged = !arraysEqual(appliedInitialState.main, initialState.main) || !arraysEqual(appliedInitialState.more, initialState.more);

		if (!initialChanged) {
			return;
		}

		setAppliedInitialState(initialState);
		setState(initialState);
		setActiveItemId(null);
		dragStartStateRef.current = null;
	}, [appliedInitialState.main, appliedInitialState.more, initialState]);

	useEffect(() => {
		onChange?.(state.main, state.more);
	}, [onChange, state.main, state.more]);

	const hasChanges = !arraysEqual(state.main, initialState.main) || !arraysEqual(state.more, initialState.more);

	const mainNavItems = state.main.map((itemId) => itemById.get(itemId)).filter(Boolean) as NavbarSettingsEditorItem[];
	const moreMenuItems = state.more.map((itemId) => itemById.get(itemId)).filter(Boolean) as NavbarSettingsEditorItem[];
	const activeItem = activeItemId ? itemById.get(activeItemId) : null;

	const handleDragStart = ({ active }: DragStartEvent) => {
		dragStartStateRef.current = cloneContainerState(stateRef.current);
		setActiveItemId(String(active.id) as NavbarItemId);
	};

	const handleDragOver = ({ active, over }: DragOverEvent) => {
		if (!over) {
			return;
		}

		setState((currentState) => {
			if (!shouldApplyDragOverOperation(currentState, active.id, over.id)) {
				return currentState;
			}

			// Cross-container dragging is applied in onDragOver so users can see
			// items move between sections in real time while dragging.
			return applyDragOperation(currentState, active.id, over.id);
		});
	};

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over) {
			if (dragStartStateRef.current) {
				setState(dragStartStateRef.current);
			}
			dragStartStateRef.current = null;
			setActiveItemId(null);
			return;
		}

		const nextState = applyDragOperation(stateRef.current, active.id, over.id);
		setState(nextState);
		dragStartStateRef.current = null;
		setActiveItemId(null);

		if (autoSaveOnDrop && !isSaving) {
			const hasDroppedChanges = !arraysEqual(nextState.main, initialState.main) || !arraysEqual(nextState.more, initialState.more);

			if (hasDroppedChanges) {
				void onSave(nextState.main, nextState.more);
			}
		}
	};

	const handleDragCancel = () => {
		if (dragStartStateRef.current) {
			setState(dragStartStateRef.current);
		}
		dragStartStateRef.current = null;
		setActiveItemId(null);
	};

	const handleSave = async () => {
		await onSave(state.main, state.more);
	};

	const handleReset = async () => {
		await onReset();
	};

	return (
		<VStack align="stretch" spacing={5}>
			{note && (
				<Box bg="info.bgAlpha" borderWidth="1px" borderColor="border.soft" borderRadius="md" px={4} py={3}>
					<Text fontSize={100} color="text.default">
						{note}
					</Text>
				</Box>
			)}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<Box display="grid" gridTemplateColumns={isStackedLayout ? '1fr' : { base: '1fr', md: '1fr 1fr' }} gap={5}>
					<SortableColumn
						droppableId={MAIN_DROPPABLE_ID}
						title={mainNavTitle}
						description={mainNavDescription}
						items={mainNavItems}
						fixedItems={fixedMainNavItems}
						dragHandlePosition={dragHandlePosition}
						dragWholeItem={dragWholeItem}
						showItemBorder={showItemBorder}
						showColumnBorder={showColumnBorder}
						columnPadding={columnPadding}
						dragHandleIcon={dragHandleIcon}
						useCompanyValuesHandleStyle={useCompanyValuesHandleStyle}
					/>
					<SortableColumn
						droppableId={MORE_DROPPABLE_ID}
						title={moreMenuTitle}
						description={moreMenuDescription}
						items={moreMenuItems}
						dragHandlePosition={dragHandlePosition}
						dragWholeItem={dragWholeItem}
						showItemBorder={showItemBorder}
						showColumnBorder={showColumnBorder}
						columnPadding={columnPadding}
						dragHandleIcon={dragHandleIcon}
						useCompanyValuesHandleStyle={useCompanyValuesHandleStyle}
					/>
				</Box>
				<DragOverlay>
					{activeItem ? (
						<Box bg="bg.surface" borderWidth="1px" borderColor="primary.fgStrong" borderRadius="md" px={3} py={2} shadow="lg">
							<HStack spacing={3}>
								{activeItem.icon && (
									<Box color="text.default" display="inline-flex">
										{activeItem.icon}
									</Box>
								)}
								<Text fontSize={100} fontWeight="medium">
									{activeItem.label}
								</Text>
								<Box ml="auto" color="info.icon" display="inline-flex" alignItems="center">
									{dragHandleIcon || <DotsVerticalOutline size="sm" />}
								</Box>
							</HStack>
						</Box>
					) : null}
				</DragOverlay>
			</DndContext>

			<HStack justifyContent="flex-end" spacing={3}>
				<Button variant="outline" onClick={handleReset} isDisabled={isSaving || isResetDisabled}>
					{resetButtonLabel}
				</Button>
				{showSaveButton && (
					<Button colorScheme="primary" onClick={handleSave} isLoading={isSaving} isDisabled={isSaving || !hasChanges}>
						{saveButtonLabel}
					</Button>
				)}
			</HStack>
		</VStack>
	);
};
